import React, {useState} from 'react';
import {useForm} from 'react-hook-form';
import {Alert, Box, Button, CircularProgress, DialogActions, DialogContent, DialogTitle, FormControlLabel,
    Switch, TextField, Typography, useMediaQuery, useTheme} from '@mui/material';
import {SettingsDialog, SettingsDialogDescription} from '../settings/SettingsDialog';
import {getSettingsColors} from '../settings/SettingsDesign';
import CurrencySelect from '../offers/CurrencySelect';
import {logServices} from '../../service/logService';
import LogCustomConversionSelect from './LogCustomConversionSelect';

const fieldGrid = {display: 'grid', gridTemplateColumns: {xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))'}, gap: 2, minWidth: 0};
const cardStyles = {p: 2, borderRadius: '12px', minWidth: 0, bgcolor: theme => getSettingsColors(theme).inset};

export default function LogConversionDialog({open, mode = 'add', clickId, onClose, onSaved}) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const formId = React.useId();
    const [loading, setLoading] = useState(false);
    const [requestError, setRequestError] = useState('');
    const [postback, setPostback] = useState(true);
    const [onlyRevenueUpdate, setOnlyRevenueUpdate] = useState(false);
    const {handleSubmit, register, control, watch, formState: {errors}} = useForm();
    const customConversion = watch('ce', '');
    const revenueOnly = !!customConversion && onlyRevenueUpdate;
    const overriding = mode === 'override';
    const actionLabel = overriding ? 'Override conversion' : 'Add conversion';
    const handleClose = (event, reason) => {
        if (reason !== 'backdropClick' && !loading) onClose();
    };
    const onSubmit = async data => {
        if (loading) return;
        setLoading(true);
        setRequestError('');
        try {
            const ce = data.ce || '';
            const updatePayoutOnly = !!ce && onlyRevenueUpdate;
            await logServices.addConversion({clickId, payout: parseFloat(data.payout), payoutCurrencyCode: data.currency?.code,
                allowPostback: postback ? (ce ? '' : 'force') : 'false',
                ...(updatePayoutOnly ? {onlyRevenueUpdateForCE: true} : {transactionId: data.transactionId, status: data.status}),
                ...(ce ? {ce} : {})});
            onSaved();
        } catch (error) {
            setRequestError(error?.response?.data?.error || 'Unable to save the conversion. Please try again.');
        } finally {setLoading(false);}
    };
    return <SettingsDialog open={open} onClose={handleClose} fullWidth fullScreen={fullScreen} maxWidth="sm" aria-labelledby="log-conversion-title"
        sx={{'& .MuiDialog-paper': {
            '& .MuiTextField-root': {my: 0},
            '& .MuiOutlinedInput-root': {bgcolor: theme => theme.palette.mode === 'light' ? theme.palette.background.paper : getSettingsColors(theme).input},
            '& .MuiOutlinedInput-input': {fontSize: 13},
            '& .MuiButton-root': {textTransform: 'none', boxShadow: 'none', minHeight: 36},
            '& .MuiAlert-root': {borderRadius: '10px', fontSize: 13, lineHeight: 1.6}
        }, '& .MuiDialogTitle-root + .MuiDialogContent-root': {pt: 1.5}}}>
        <DialogTitle id="log-conversion-title">{actionLabel}
            <SettingsDialogDescription>{overriding ? 'Update conversion details for the selected click.' : 'Record a conversion for the selected click.'}</SettingsDialogDescription>
        </DialogTitle>
        <DialogContent>
            {requestError && <Alert severity="error" sx={{mb: 2}}>{requestError}</Alert>}
            <Box component="form" id={formId} onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                <Box component="fieldset" disabled={loading} sx={{border: 0, p: 0, m: 0, minWidth: 0, display: 'grid', gap: 2}}>
                    <Box sx={{...cardStyles, py: 1.5}}>
                        <Typography sx={{fontSize: 11, color: 'text.secondary', mb: 0.5}}>Click ID</Typography>
                        <Typography sx={{fontFamily: 'monospace', fontSize: 13, fontWeight: 500, color: 'text.primary', overflowWrap: 'anywhere', userSelect: 'all'}}>{clickId}</Typography>
                    </Box>
                    <Box component="section" aria-labelledby="log-conversion-details" sx={cardStyles}>
                        <Typography component="h3" id="log-conversion-details" sx={{fontSize: 14, fontWeight: 600, mb: 2}}>Conversion details</Typography>
                        <Box sx={{display: 'grid', gap: 2}}>
                            <LogCustomConversionSelect control={control} disabled={loading} onChange={() => setOnlyRevenueUpdate(false)}/>
                            <Box sx={fieldGrid}>
                                <TextField {...register('payout', {required: 'Enter a payout amount.', pattern: {value: /^(\d+)(?:[.]\d+)?$/, message: 'Enter a valid amount, such as 5.40.'}})}
                                    label="Payout *" placeholder="e.g. 5.40" fullWidth size="small" error={!!errors.payout} helperText={errors.payout?.message}
                                    slotProps={{inputLabel: {shrink: true}, htmlInput: {inputMode: 'decimal'}}}/>
                                <CurrencySelect control={control}/>
                            </Box>
                            <Box sx={fieldGrid}>
                                <TextField {...register('status', {disabled: revenueOnly, maxLength: {value: 255, message: 'Use at most 255 characters.'}})}
                                    label="Status (optional)" placeholder="e.g. lead" fullWidth size="small" disabled={revenueOnly} error={!!errors.status} helperText={errors.status?.message}
                                    slotProps={{inputLabel: {shrink: true}}}/>
                                <TextField {...register('transactionId', {disabled: revenueOnly, maxLength: {value: 255, message: 'Use at most 255 characters.'}})}
                                    label="Transaction ID (optional)" placeholder="e.g. order-12345" fullWidth size="small" disabled={revenueOnly} error={!!errors.transactionId} helperText={errors.transactionId?.message}
                                    slotProps={{inputLabel: {shrink: true}}}/>
                            </Box>
                        </Box>
                    </Box>
                    {customConversion && <Box sx={cardStyles}>
                        <FormControlLabel labelPlacement="start" sx={{m: 0, width: '100%', justifyContent: 'space-between', gap: 1.5}}
                            label={<Box><Typography sx={{fontSize: 13, fontWeight: 600}}>Update existing payout only</Typography>
                                <Typography sx={{fontSize: 12, color: 'text.secondary', mt: 0.5}}>Replace the total payout of existing conversions for this event without creating a new conversion.</Typography></Box>}
                            control={<Switch checked={onlyRevenueUpdate} onChange={event => setOnlyRevenueUpdate(event.target.checked)} disabled={loading}
                                slotProps={{input: {'aria-label': 'Update existing payout only'}}} sx={{flexShrink: 0, mr: -1}}/>}/>
                    </Box>}
                    <Box sx={cardStyles}>
                        <FormControlLabel labelPlacement="start" sx={{m: 0, width: '100%', justifyContent: 'space-between', gap: 1.5, alignItems: 'center'}}
                            label={<Box><Typography sx={{fontSize: 13, fontWeight: 600}}>Run postback to traffic source</Typography>
                                <Typography sx={{fontSize: 12, color: 'text.secondary', mt: 0.5}}>Send a conversion notification to the traffic source.</Typography></Box>}
                            control={<Switch checked={postback} onChange={event => setPostback(event.target.checked)}
                                slotProps={{input: {'aria-label': 'Run postback to traffic source'}}} sx={{flexShrink: 0, mr: -1}}/>}/>
                    </Box>
                    <Alert severity="info">{customConversion
                        ? (revenueOnly ? 'Only the payout is updated. If no matching custom conversion exists, no new conversion will be created.'
                            : 'A new custom conversion will be recorded for this click ID.')
                        : 'An existing standard conversion is overwritten unless the affiliate network allows multiple conversions.'}</Alert>
                </Box>
            </Box>
        </DialogContent>
        <DialogActions>
            <Button type="button" color="inherit" disabled={loading} onClick={handleClose}>Cancel</Button>
            <Button type="submit" form={formId} variant="contained" disabled={loading}
                startIcon={loading ? <CircularProgress size={16} color="inherit"/> : undefined}>{loading ? 'Saving…' : revenueOnly ? 'Update payout' : actionLabel}</Button>
        </DialogActions>
    </SettingsDialog>;
}
